// firebase/callable.ts
// Typed wrapper around httpsCallable that attaches a UUID idempotency key
// to every logical operation. Retries (same call site + same input) MUST
// reuse the key, so callers may pass an explicit `idempotencyKey` when
// wrapping their own retry loop; otherwise one is generated per call.
//
// The key is merged onto the payload as `__idempotencyKey` — a flat, ignorable
// field. Backends that don't honour it (Zod's default z.object strips unknown
// keys) still see their expected shape at request.data top-level; backends
// that DO honour it read request.data.__idempotencyKey and dedupe on it.

import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { v4 as uuidv4 } from 'uuid';
import { functions } from './config';

export const IDEMPOTENCY_KEY_FIELD = '__idempotencyKey' as const;

export type WithIdempotencyKey<Req> = Req & {
    readonly [IDEMPOTENCY_KEY_FIELD]: string;
};

export interface CallOptions {
    /**
     * Explicit idempotency key. If omitted, a UUID v4 is generated per call.
     * Reuse the same key on any client-side retry of the same logical operation.
     */
    idempotencyKey?: string;
}

/**
 * Generate a fresh idempotency key. Callers that implement their own retry
 * should generate the key once per logical op and reuse it on every retry.
 */
export function newIdempotencyKey(): string {
    return uuidv4();
}

/**
 * Invoke an httpsCallable with a UUID idempotency key attached.
 * The key is merged onto the payload as `__idempotencyKey` so backends see
 * their normal top-level fields at request.data and can pluck the key out
 * without an envelope change.
 */
export async function callWithIdempotency<Req extends object, Res>(
    name: string,
    data: Req,
    options: CallOptions = {}
): Promise<Res> {
    const idempotencyKey = options.idempotencyKey ?? newIdempotencyKey();
    const fn: HttpsCallable<WithIdempotencyKey<Req>, Res> = httpsCallable(functions, name);
    const payload = { ...data, [IDEMPOTENCY_KEY_FIELD]: idempotencyKey } as WithIdempotencyKey<Req>;
    const result = await fn(payload);
    return result.data;
}

/**
 * Return a bound callable that always reuses the given idempotency key.
 * Convenience for a caller-managed retry loop:
 *
 *   const key = newIdempotencyKey();
 *   const call = boundCallable<Req, Res>('setAdminRole', { idempotencyKey: key });
 *   await retry(() => call(payload));
 */
export function boundCallable<Req extends object, Res>(
    name: string,
    options: Required<CallOptions>
): (data: Req) => Promise<Res> {
    return (data: Req) =>
        callWithIdempotency<Req, Res>(name, data, { idempotencyKey: options.idempotencyKey });
}
